import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { toOrgDateKey } from "../lib/timezone";
import { CACHE_VERSION } from "../lib/cacheVersion";

// =============================================================================
// useFunnelHealth — Funnel Attribution Metrics
// =============================================================================
// PURPOSE: Fetches funnel stage metrics (leads_in → booked → shown → closed)
// from get_funnel_metrics RPC, both merged and split by source.
//
// RETURNS: { merged, bySource, isLoading }
// - merged: combined pipeline counts + revenue/cash
// - bySource: per-funnel metrics with close_rate, aov, show_rate
//
// CACHING: localStorage with 10-min TTL (live ranges), 24h (historical).
// get_funnel_metrics is 317ms+ on 28K rows — caching prevents re-fire on every nav.
// =============================================================================

const FUNNEL_CACHE_PREFIX = `altara_${CACHE_VERSION}_funnel_`;
const FUNNEL_CACHE_TTL = 10 * 60 * 1000; // 10 minutes — live ranges
const FUNNEL_HISTORICAL_TTL = 24 * 60 * 60 * 1000; // 24 hours — historical ranges

interface FunnelCacheEntry {
  data: any;
  timestamp: number;
  orgId: string;
}

function getFunnelCache(key: string, orgId: string): any | null {
  try {
    const raw = localStorage.getItem(FUNNEL_CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: FunnelCacheEntry = JSON.parse(raw);
    // Determine TTL based on whether the date range ends before today
    const dateMatch = key.match(/(\d{4}-\d{2}-\d{2})/g);
    let ttl = FUNNEL_CACHE_TTL;
    if (dateMatch && dateMatch.length >= 2) {
      const endDate = new Date(dateMatch[1] + "T23:59:59");
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (endDate < today) ttl = FUNNEL_HISTORICAL_TTL;
    }
    if (Date.now() - entry.timestamp > ttl || entry.orgId !== orgId) {
      localStorage.removeItem(FUNNEL_CACHE_PREFIX + key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

function setFunnelCache(key: string, data: any, orgId: string): void {
  try {
    localStorage.setItem(
      FUNNEL_CACHE_PREFIX + key,
      JSON.stringify({ data, timestamp: Date.now(), orgId })
    );
  } catch {
    /* localStorage full — ignore */
  }
}

export interface FunnelStageData {
  leads_in: number;
  booked: number;
  shown: number;
  closed: number;
  low_ticket: number;
  revenue: number;
  cash: number;
  qualified: number;
  dq: number;
  // LT exit classification
  lt_dq_exit: number;
  lt_dialer_exit: number;
  lt_downsell_exit: number;
  // Attribution: leads with outcomes in period but entry before period
  attributed_from_prior: number;
  // Pre-calls and cancellations (from sales_pre_call and call_cancelled events)
  pre_calls_filled: number;
  cancellations: number;
  // Owner split of pre_calls_filled (Jun 23, 2026) — /precall resolution, merged only.
  // setter + dialer + self = pre_calls_filled.
  pre_calls_setter_owned?: number;
  pre_calls_dialer_owned?: number;
  pre_calls_self_booked?: number;
}

export interface FunnelSourceData extends FunnelStageData {
  show_rate: number;
  close_rate: number;
  aov: number;
  dq_rate: number;
  total_signups?: number;
  unique_leads?: number;
  total_cash?: number;
  // Pre-call compliance gap — how many booked leads didn't fill the pre-call form
  missing_precalls?: number;
  // Webinar attendance breakdown
  attended?: number;
  missed?: number;
  captured?: number;
  // Webinar sub-columns: discovery vs upsell path
  webinar_discovery_booked?: number;
  webinar_discovery_shown?: number;
  webinar_discovery_closed?: number;
  webinar_discovery_revenue?: number;
  webinar_discovery_cash?: number;
  webinar_upsell_booked?: number;
  webinar_upsell_shown?: number;
  webinar_upsell_closed?: number;
  webinar_upsell_revenue?: number;
  webinar_upsell_cash?: number;
  // Typeform sub-columns: qualified vs revived DQ path
  typeform_qual_booked?: number;
  typeform_qual_shown?: number;
  typeform_qual_closed?: number;
  typeform_revived_booked?: number;
  typeform_revived_shown?: number;
  typeform_revived_closed?: number;
  // Typeform dual-grain (May 2026): submission count vs distinct lead count
  applied_submissions?: number;
  applied_leads?: number;
  qualified_submissions?: number;
  qualified_leads?: number;
  dq_submissions?: number;
  dq_leads?: number;
}

export interface ReturningDistEntry {
  signup_number: number;
  count: number;
}

export interface ReturningByDateEntry {
  webinar_date: string;
  returning_count: number;
}

export interface FunnelHealthData {
  merged: FunnelStageData;
  bySource: Record<string, FunnelSourceData>;
  returningDistribution: ReturningDistEntry[];
  returningByDate: ReturningByDateEntry[];
  isLoading: boolean;
}

const EMPTY_MERGED: FunnelStageData = {
  leads_in: 0,
  booked: 0,
  shown: 0,
  closed: 0,
  low_ticket: 0,
  revenue: 0,
  cash: 0,
  qualified: 0,
  dq: 0,
  lt_dq_exit: 0,
  lt_dialer_exit: 0,
  lt_downsell_exit: 0,
  attributed_from_prior: 0,
  pre_calls_filled: 0,
  cancellations: 0,
  pre_calls_setter_owned: 0,
  pre_calls_dialer_owned: 0,
  pre_calls_self_booked: 0,
};

// Combined state interface — batches all funnel data into a single setState to avoid
// 4 separate re-renders when the RPC resolves.
interface FunnelState {
  merged: FunnelStageData;
  bySource: Record<string, FunnelSourceData>;
  returningDistribution: ReturningDistEntry[];
  returningByDate: ReturningByDateEntry[];
  isLoading: boolean;
}

const INITIAL_FUNNEL_STATE: FunnelState = {
  merged: EMPTY_MERGED,
  bySource: {},
  returningDistribution: [],
  returningByDate: [],
  isLoading: false,
};

export function useFunnelHealth(
  organizationId: string | undefined | null,
  dateRange: { start: Date | null; end: Date | null }
): FunnelHealthData {
  const [funnelState, setFunnelState] = useState<FunnelState>(INITIAL_FUNNEL_STATE);
  const abortRef = useRef<AbortController | null>(null);

  const fetchFunnel = useCallback(async () => {
    if (!organizationId || !dateRange.start || !dateRange.end || !supabase) {
      setFunnelState(INITIAL_FUNNEL_STATE);
      return;
    }

    // Abort previous request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const startStr = toOrgDateKey(dateRange.start);
    const endStr = toOrgDateKey(dateRange.end);
    const cacheKey = `${organizationId}_${startStr}_${endStr}`;

    // Check cache first — avoids 317ms+ RPC on every navigation
    const cached = getFunnelCache(cacheKey, organizationId);
    if (cached) {
      setFunnelState({
        merged: cached.merged || EMPTY_MERGED,
        bySource: cached.by_source || {},
        returningDistribution: cached.returning_distribution || [],
        returningByDate: cached.returning_by_date || [],
        isLoading: false,
      });
      return;
    }

    setFunnelState((prev) => ({ ...prev, isLoading: true }));

    try {
      const attrResult = await supabase.rpc("get_funnel_metrics", {
        p_organization_id: organizationId,
        p_start_date: startStr,
        p_end_date: endStr,
      });

      if (controller.signal.aborted) return;

      if (attrResult.error) {
        console.warn("Funnel metrics error:", attrResult.error);
        setFunnelState((prev) => ({ ...prev, isLoading: false }));
      } else if (attrResult.data) {
        // Write to cache unconditionally (even if unmounted — prevents cold cache on next visit)
        setFunnelCache(cacheKey, attrResult.data, organizationId);
        // Single setState — one re-render instead of 4
        setFunnelState({
          merged: attrResult.data.merged || EMPTY_MERGED,
          bySource: attrResult.data.by_source || {},
          returningDistribution: attrResult.data.returning_distribution || [],
          returningByDate: attrResult.data.returning_by_date || [],
          isLoading: false,
        });
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      console.warn("Funnel metrics fetch failed:", err);
      setFunnelState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [organizationId, dateRange.start, dateRange.end]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchFunnel();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchFunnel]);

  return funnelState;
}
